import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import Breadcrumbs, { Crumb } from 'components/ui/Breadcrumbs';
import { useDispatch, useSelector } from 'react-redux';
import { routes as ROUTES } from 'constants/config';
import request from 'utils/request_ajax';
import { Link, MoreHorizontal, Trash2 } from 'react-feather';
import { useFeatureFlags, useSessionStorage } from 'hooks';
import { Spinner } from 'ui';
import copyTextToClipboard from 'utils/copy_text';
import { updateBanner, displayBanner } from '../../actions/journeyBuilder';

const Topbar = () => {
  const { title, id } = useSelector(state => state);
  const [showLinkCopied, updateShowLinkCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggleMore, setToggleMore] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [copyHostname, setCopyHostname] = useState(window.location.origin);

  const isIframedInSeismic = useSessionStorage('iframedInSeismic');
  const hasUnificationFeatureFlag = useFeatureFlags(
    'readiness-enable-unification',
  );

  const dispatch = useDispatch();

  const { journeyId } = useParams();

  const handleSeismicUrlListener = async event => {
    if (
      !event.origin.endsWith('seismic.com') &&
      !event.origin.endsWith('seismic-dev.com')
    ) {
      return;
    }

    // ignore browser extensions that are sending messages.
    if (window === event.source) {
      return;
    }

    if (event.data?.domain?.includes('seismic')) {
      setCopyHostname(event.origin);
    }
  };

  useEffect(() => {
    if (window.location !== window.parent.location) {
      window.addEventListener('message', handleSeismicUrlListener);
    }

    return () => {
      window.removeEventListener('message', handleSeismicUrlListener);
    };
  }, []);

  const handleDelete = () => {
    setShowAlert(true);

    if (
      /* eslint-disable-next-line no-alert */
      window.confirm(t('journeys.builder.delete_confirm'))
    ) {
      setDeleting(true);
      request
        .delete(ROUTES.JOURNEY.DELETE(journeyId))
        .then(() => {
          window.location.assign(ROUTES.CONTENT.INDEX);
        })
        .fail(() => {
          dispatch(
            updateBanner({
              message: t('journeys.builder.request_error'),
              status: 'error',
            }),
          );
          setDeleting(false);
          dispatch(displayBanner());
        });
      setDeleting(false);
      setToggleMore(false);
      setShowAlert(!showAlert);
    }
  };

  const getJourneyUrl = () => {
    if (window.location !== window.parent.location) {
      return `${copyHostname}/app/#/learning/journey/${id}`;
    }

    return `${copyHostname}/journey/${id}`;
  };

  const handleCopyToClipboard = () => {
    copyTextToClipboard(
      getJourneyUrl(),
      () => {},
      () => {},
    );

    updateShowLinkCopied(true);

    setTimeout(() => {
      updateShowLinkCopied(false);
    }, 2000);
  };

  return (
    <div
      className={classNames('JourneyReporting-topBar', {
        'JourneyReporting-topBar--iFrame':
          isIframedInSeismic && hasUnificationFeatureFlag,
      })}
    >
      <Breadcrumbs
        parentUrl="/content"
        parentTitle={t('journeys.builder.content')}
        className="mr-24 mt-1 mb-1"
      >
        <Crumb>{title}</Crumb>
      </Breadcrumbs>
      <div className="JourneyBuilderTopbar-actionButtons">
        <button
          type="button"
          className="JourneyBuilderTopbar-share"
          onClick={handleCopyToClipboard}
        >
          <>
            {/* eslint-disable-next-line */}
            <Link size={16} />
            {showLinkCopied
              ? t('journeys.builder.link_copied')
              : t('journeys.builder.share_link')}
          </>
        </button>
        <button
          type="button"
          className="JourneyBuilderTopbar-more"
          onClick={() => {
            setToggleMore(!toggleMore);
          }}
        >
          <MoreHorizontal
            className="JourneyBuilderTopbar-icon-more"
            size={16}
          />
        </button>
        {toggleMore && (
          <button
            type="button"
            className="JourneyBuilderTopbar-delete-published"
            onClick={handleDelete}
          >
            {deleting && (
              <div className="JourneyBuilderTopbar-deleting">
                <Spinner />
              </div>
            )}
            <Trash2 className="JourneyBuilderTopbar-icon--alt" size={16} />
            {t('journeys.builder.delete')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Topbar;
